import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Create axios instance with auth header
const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Add token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const adminService = {
  // ============================================
  // Dashboard & Statistics
  // ============================================
  
  getDashboardStats: async () => {
    try {
      const response = await axiosInstance.get('/admin/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // User Management
  // ============================================
  
  getAllUsers: async () => {
    try {
      const response = await axiosInstance.get('/admin/users');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUserDetails: async (userId) => {
    try {
      const response = await axiosInstance.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const response = await axiosInstance.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateUserRole: async (userId, role) => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/role`, {
        role
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateUserStorage: async (userId, storageLimitGB) => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/storage`, {
        storage_limit: storageLimitGB
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reset user password (admin only)
  resetUserPassword: async (userId, newPassword) => {
    try {
      const response = await axiosInstance.post(`/admin/users/${userId}/reset-password`, {
        newPassword
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // NEW: Registration Approval
  // ============================================
  
  getPendingRegistrations: async () => {
    try {
      const response = await axiosInstance.get('/admin/pending-registrations');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  approveUser: async (userId) => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/approve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  rejectUser: async (userId, reason) => {
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/reject`, {
        reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // Storage & Analytics
  // ============================================
  
  getStorageByUser: async (limit = 10) => {
    try {
      const response = await axiosInstance.get(`/admin/storage-by-user?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getFileTypeDistribution: async () => {
    try {
      const response = await axiosInstance.get('/admin/file-types');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRecentActivities: async (limit = 20) => {
    try {
      const response = await axiosInstance.get(`/admin/activities?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStorageUsageOverTime: async (days = 30) => {
    try {
      const response = await axiosInstance.get(`/admin/storage-usage?days=${days}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUserGrowth: async (days = 30) => {
    try {
      const response = await axiosInstance.get(`/admin/user-growth?days=${days}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default adminService;
